import React, { useMemo } from 'react';
import { formatDateToView } from '../lib/DateTime';

const VehicleChecksDisplay = ({ checks, theme }) => {
  const greenTickStyle = { color: 'green' };
  const redCrossStyle = { color: 'red' };
  
  // Helper function to apply the header rotation style conditionally
  const getHeaderStyle = (header, isDefectType = false) => {
    return (header.endsWith('OK') || isDefectType)
      ? {
          transform: 'rotate(-55deg) translateX(-55px) translateY(40px)', // Rotate and translate for space
          whiteSpace: 'nowrap',
          maxWidth: '30px', // Adjust width for space
          height: '175px', // Adjust height for space
          textAlign: 'center',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '10px',
        }
      : { textAlign: 'center', verticalAlign: 'bottom', padding: '10px' }; // Regular style for non-'OK' headers
  };

  const checkStatus = (status) => {
    if (status === 'passed') {
      return { color: 'rgba(174, 247, 138)' };
    } else if (status === 'repaired') {
      return { color: 'rgba(25, 151, 235)' };
    } else {
      return { color: 'rgba(247, 138, 138)' };
    }
  };

  const columnStyling = (order) => {
    return {
      padding: '10px 0',
      backgroundColor: order % 2 === 0 ? 'rgba(203, 205, 205, 0.2)' : 'rgba(2, 204, 255, 0.3)',
    };
  };

  // Helper function to render either the green tick or red cross, or empty string if null
  const renderCheck = (checkValue) => {
    if (checkValue === null || checkValue === undefined) {
      return ''; // Return empty string if the check value is null
    }
    return checkValue ? <span style={greenTickStyle}>✔️</span> : <span style={redCrossStyle}>❌</span>;
  };

  // Get all unique defect types from all checks to build dynamic headers
  const allDefectTypes = useMemo(() => {
    const defectTypeMap = new Map();
    
    checks.forEach(check => {
      if (check.available_defect_types) {
        check.available_defect_types.forEach(defectType => {
          if (!defectTypeMap.has(defectType.id)) {
            defectTypeMap.set(defectType.id, {
              id: defectType.id,
              defect: defectType.defect,
              position: defectType.position || 999
            });
          }
        });
      }
    });
    
    // Convert map to array and sort by position
    return Array.from(defectTypeMap.values()).sort((a, b) => a.position - b.position);
  }, [checks]);

  // Helper function to check if a defect type is available for a specific check's vehicle type
  const isDefectTypeAvailableForCheck = (check, defectTypeId) => {
    return check.available_defect_types && check.available_defect_types.some(dt => dt.id === defectTypeId);
  };

  // Helper function to check if a defect type failed (is in failed_defect_type_ids)
  const hasDefectTypeFailed = (check, defectTypeId) => {
    return check.failed_defect_type_ids && check.failed_defect_type_ids.includes(defectTypeId);
  };

  // Helper function to determine if all available defect types passed for a check
  const getAllPassedStatus = (check) => {
    if (!check.available_defect_types || check.available_defect_types.length === 0) {
      return check.all_passed; // Fall back to database value if no defect types configured
    }
    
    // Check if any of the available defect types failed
    const hasAnyFailed = check.available_defect_types.some(defectType => 
      hasDefectTypeFailed(check, defectType.id)
    );
    
    return !hasAnyFailed; // All passed if none failed
  };

  return (
    <div>
      <table border="1" cellPadding="5" cellSpacing="0">
        <thead>
          <tr style={{ justifyContent: 'flex-start', alignSelf: 'baseline' }}>
            <th style={getHeaderStyle('Registration')}>Reg</th>
            <th style={getHeaderStyle('Driver')}>Driver</th>
            <th style={getHeaderStyle('Date')}>Date</th>
            <th style={getHeaderStyle('Duration')}>Duration</th>
            <th style={getHeaderStyle('Status')}>Status</th>
            <th style={getHeaderStyle('Mileage')}>Mileage</th>
            {/* Dynamic defect type headers */}
            {allDefectTypes.map(defectType => (
              <th key={defectType.id} style={getHeaderStyle(defectType.defect, true)}>
                {defectType.defect}
              </th>
            ))}
            <th style={getHeaderStyle('All Passed')}>All Passed</th>
            <th style={getHeaderStyle('Defects')}>Defects</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((check, checkIndex) => (
            <tr key={check.vehicle_checks_uuid || check.id || checkIndex} style={{ border: theme === 'dark' ? '1px dotted white' : '1px dotted black', padding: '10px 0' }}>
              <td style={{ padding: '10px 5px' }}>{check.reg}</td>
              <td style={{ padding: '10px 5px' }}>{check.name}</td>
              <td style={{ padding: '10px 0' }}>{formatDateToView(check.date)}</td>
              <td style={{ padding: '10px 0' }}>{check.duration || 'N/A'}</td>
              <td style={{ ...checkStatus(check.check_status), padding: '10px 0' }}>{check.check_status}</td>
              <td style={{ padding: '10px 0' }}>{check.mileage || 'N/A'}</td>
              
              {/* Dynamic defect type columns */}
              {allDefectTypes.map((defectType, index) => {
                // Check if this defect type is available for this vehicle type
                const isAvailable = isDefectTypeAvailableForCheck(check, defectType.id);
                
                if (!isAvailable) {
                  // Leave blank if this defect type doesn't apply to this vehicle type
                  return (
                    <td key={defectType.id} style={columnStyling(index + 1)}>
                    </td>
                  );
                }
                
                // Show ❌ if defect type failed, ✔️ if it passed
                const hasFailed = hasDefectTypeFailed(check, defectType.id);
                return (
                  <td key={defectType.id} style={columnStyling(index + 1)}>
                    {renderCheck(!hasFailed)}
                  </td>
                );
              })}
              
              {/* All Passed column */}
              <td style={columnStyling(0)}>{renderCheck(getAllPassedStatus(check))}</td>
              
              <td style={{ padding: '10px 0', minWidth: '300px', maxWidth: '500px', wordWrap: 'break-word' }}>
                {check.defects && check.defects.length > 0 ? (
                  check.defects.map((defect, index) => {
                    const defectTypeName = defect.defect_type_name || defect.defect_types?.defect || 'Unknown Defect';
                    return (
                      <div key={defect.id || index}>
                        {defect.repairer_name ? 
                          `${defectTypeName}: (Repaired: ${defect.repairer_name} - ${defect.repairer_comment || 'No comment'})` :
                          `${defectTypeName}: ${defect.status} - ${defect.comments || 'No comment'}`
                        }
                      </div>
                    );
                  })
                ) : (
                  <span>No defects found!</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VehicleChecksDisplay;
